import React from "react";
import { noop } from "lodash";
import { NavLink } from "react-router-dom";
import classNames from "classnames";
import {
  BellIcon,
  ChartPieIcon,
  PresentationChartBarIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { useAllNotification } from "@hooks/query/useAllNotifications";
import { useAppContext } from "@context/AppContext";
import { VIEW_TRANSACTIONS_PERMISSIONS } from "@config/zenithPermissions";
import { useSubscriberLevel0Resources } from "@hooks/query/zenith/useSubscriberLevel0Resources";

type NavItem = {
  name: string;
  href: string;
  icon: React.ComponentType<React.ComponentProps<"svg">>;
  end?: boolean;
  badge?: number;
  permissions?: string[];
  testId?: string;
};

type AppNavigationProps = {
  isMobile?: boolean;
  onClose?: () => void;
};

const AppNavigation: React.FunctionComponent<AppNavigationProps> = ({
  isMobile,
  onClose = noop,
}) => {
  const { t } = useTranslation();
  const {
    isExpandSidebar,
    checkPermissions,
    loggedInInfo: { userDetails },
  } = useAppContext();
  const { data: level0Balances = [] } = useSubscriberLevel0Resources();
  const { data: { data: unreadNotifications = [] } = {} } = useAllNotification({
    params: {
      recipientWalletId: userDetails?.walletId,
      onlyNew: 1,
      limit: -1,
    },
    enabled: Boolean(userDetails?.walletId),
  });
  const showLabels = isExpandSidebar || isMobile;

  const groups: { name: string; items: NavItem[] }[] = [
    {
      name: t("zenith.navigation.general"),
      items: [
        {
          name: t("zenith.navigation.scheme_dashboard"),
          href: "/zenith",
          icon: PresentationChartBarIcon,
          end: true,
        },
        {
          name: t("zenith.navigation.dashboard"),
          href: "/zenith/dashboard",
          icon: Squares2X2Icon,
        },
      ],
    },
    {
      name: t("zenith.navigation.my_water"),
      items: [
        {
          name: t("zenith.navigation.balances"),
          href: "/zenith/balances",
          icon: ChartPieIcon,
        },
        ...level0Balances.map((l0wrs: any) => ({
          name: l0wrs.identifier,
          href: `/zenith/zone_accounts/${l0wrs.id}`,
          icon: WalletIcon,
          // Named by its identifier alone, a zone link has no role to find it by.
          testId: "zenith-zone-link",
        })),
      ],
    },
    {
      name: t("zenith.navigation.apps"),
      items: [
        {
          name: t("zenith.navigation.notifications"),
          href: "/zenith/notifications",
          icon: BellIcon,
          badge: unreadNotifications.length,
        },
        {
          name: t("zenith.navigation.workflows"),
          href: "/zenith/workflows",
          icon: RectangleStackIcon,
        },
        {
          name: t("zenith.navigation.audit_trail"),
          href: "/zenith/audit_trail",
          icon: ShieldCheckIcon,
          permissions: VIEW_TRANSACTIONS_PERMISSIONS,
        },
      ],
    },
  ];

  return (
    <nav className="space-y-4" onClick={onClose}>
      {groups.map(group => (
        <div key={group.name}>
          <h2
            className={classNames(
              "mb-1 px-2 text-xxs font-bold uppercase tracking-widest text-gray-500",
              { "sr-only": !showLabels },
            )}
          >
            {group.name}
          </h2>
          <ul className="space-y-0.5">
            {group.items
              .filter(
                item => !item.permissions || checkPermissions(item.permissions),
              )
              .map(item => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    end={item.end}
                    title={item.name}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      classNames(
                        "relative flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm",
                        isActive
                          ? "bg-primary-1/10 font-semibold text-primary-1"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive ? (
                          <span
                            className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-r bg-primary-1"
                            aria-hidden="true"
                          />
                        ) : null}
                        <item.icon
                          className="h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        <span
                          className={classNames("flex-1 truncate", {
                            "sr-only": !showLabels,
                          })}
                        >
                          {item.name}
                        </span>
                        {item.badge && showLabels ? (
                          <span className="rounded-full bg-red-500 px-1.5 text-xxs font-bold leading-4 text-white">
                            {item.badge}
                          </span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export default AppNavigation;
